import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPermission } from 'api';
import { ANALYST_VALUE, COMPANY, EDIT, FIRM, FUND, USE_PORTAL } from 'common/constants/user';
import { AddUserToFirmResponse } from 'common/types/user';
import { transformPermissions, translateRoleIntoPermissions } from 'pages/UserPermissions/utils';
import { FirmService, UsersService } from 'services';
import {
  AddUserToFirmParams,
  ApiError,
  ChangeUserFirmRoleParams,
  CreateBulkUsersParams,
  RemoveUserFromFirmParams,
  SaveUserPermissionsParams,
} from './types';
import useResponse from '../useResponse';

const addUserToFirm = async ({ firmId, userData, permissions }: AddUserToFirmParams) => {
  const firmService = new FirmService();
  const response = await firmService.addUserToFirm(firmId, userData) as AddUserToFirmResponse;
  if (response) {
    const usersService = new UsersService();
    const transformedPermissions = transformPermissions({ permissions });
    const permissionsToSave = transformedPermissions.map((p: UserPermission) => ({
      ...p,
      firm_role: response.firm_role,
    }));
    await usersService.savePermissions([
      {
        user_id: response.new_user,
        permissions: permissionsToSave,
        role_type: response.role,
        firm_role: response.firm_role,
        skip_user_firm_role_creation: true,
      },
    ]);
  }
  if (!response) {
    throw new Error('An error occurred while adding user to firm');
  }
  return response;
};

const useAddUserToFirm = () => {
  const queryClient = useQueryClient();
  const addFirmUserMutation = useMutation({
    mutationFn: ({ firmId, userData, permissions }: AddUserToFirmParams) =>
      addUserToFirm({
        firmId,
        userData,
        permissions,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['firmUsers']);
    },
    onError: (error: ApiError) => {
      const userErrors = error?.response?.body?.User;

      const errorMsg
        = Array.isArray(userErrors) && userErrors.length > 0
          ? userErrors[0]
          : 'An error occurred while adding user to firm';

      throw new Error(errorMsg);
    },
  });

  const { mutate, mutateAsync, isLoading, error, isSuccess } = addFirmUserMutation;

  if (isSuccess) {
    addFirmUserMutation.reset();
  }

  return { mutate, mutateAsync, isLoading, error, isSuccess };
};

const removeUserFromFirm = async ({ firm_id, user_id }: RemoveUserFromFirmParams) => {
  const firmService = new FirmService();
  const response = await firmService.removeUserFromFirm(firm_id, user_id);
  if (!response) {
    throw new Error('An error occurred while removing user from firm');
  }
  return response;
};

const useRemoveUserFromFirm = () => {
  const queryClient = useQueryClient();
  const removeFirmUserMutation = useMutation({
    mutationFn: ({ firm_id, user_id }: RemoveUserFromFirmParams) =>
      removeUserFromFirm({
        firm_id,
        user_id,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firmUsers'] as QueryKey });
    },
  });

  const { mutate, isLoading, error, isSuccess } = removeFirmUserMutation;

  if (isSuccess) {
    removeFirmUserMutation.reset();
  }

  return { mutate, isLoading, error, isSuccess };
};

const createBulkUsers = async ({
  firm_name,
  firm_id,
  users,
  companyList,
  fundList,
  skipEmail = false,
}: CreateBulkUsersParams) => {
  const data = {
    firm_name,
    firm_id,
    users,
    role_id: ANALYST_VALUE,
    skip_email: skipEmail,
  };
  const usersService = new UsersService();
  const response = await usersService.createBulkUsers(data);
  if (!response) {
    throw new Error('An error occurred while creating users');
  }
  const roleToPermissions = translateRoleIntoPermissions({
    role: { id: ANALYST_VALUE, firmItem: { itemId: null, itemType: null } },
    firmId: firm_id,
    companyList,
    fundList,
  });

  const transformedPermissions = transformPermissions({ permissions: roleToPermissions });

  const usersAndPermissions = response.map((user: { id: number; firm_role_id: number }) => ({
    user_id: user.id,
    permissions: transformedPermissions,
    firm_id,
    role_type: ANALYST_VALUE,
    firm_role: user.firm_role_id,
    skip_user_firm_role_creation: true,
  }));

  await usersService.savePermissions(usersAndPermissions);

  return response;
};

const useCreateBulkUsers = () => {
  const queryClient = useQueryClient();
  const createBulkUsersMutation = useMutation({
    mutationFn: ({ firm_name, firm_id, users, companyList, fundList, skipEmail = false }: CreateBulkUsersParams) =>
      createBulkUsers({
        firm_name,
        firm_id,
        users,
        companyList,
        fundList,
        skipEmail,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['firmUsers']);
    },
  });

  const { mutate, isLoading, error, isSuccess } = createBulkUsersMutation;

  if (isSuccess) {
    createBulkUsersMutation.reset();
  }

  return { mutate, isLoading, error, isSuccess };
};

const changeUserFirmRole = async ({ firmId, userId, roleId }: ChangeUserFirmRoleParams) => {
  const firmService = new FirmService();
  const response = await firmService.changeUserFirmRole(firmId, userId, roleId);
  if (!response) {
    throw new Error('An error occurred while changing user firm role');
  }
  return response;
};

const useChangeUserFirmRole = () => {
  const queryClient = useQueryClient();
  const changeUserFirmRoleMutation = useMutation({
    mutationFn: ({ firmId, userId, roleId }: ChangeUserFirmRoleParams) =>
      changeUserFirmRole({
        firmId,
        userId,
        roleId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['firmUsers']);
    },
  });

  const { mutate, isLoading, error, isSuccess } = changeUserFirmRoleMutation;

  if (isSuccess) {
    changeUserFirmRoleMutation.reset();
  }

  return { mutate, isLoading, error, isSuccess };
};

const updatePermissionsForCompOrFundRole = (
  itemType: string,
  itemId: number,
  prevPermissions: any,
  roleType: number,
  selectedFirm: any,
  userSelected: any
) => {
  const updatedPermissions = prevPermissions.permissions
    .filter((perm: any) => perm.id !== 0)
    .map((perm: any) => ({
      ...perm,
      remove_permission: true,
    }));

  const newPermission = {
    id: 0,
    feature_object: {
      feature: null,
      object_type: itemType,
      object_id: itemId,
    },
    access_type: itemType === COMPANY ? USE_PORTAL : EDIT,
    remove_permission: false,
  };

  updatedPermissions.push(newPermission);

  if (itemType === FUND) {
    const firmPermission = {
      id: 0,
      feature_object: {
        feature: null,
        object_type: FIRM,
        object_id: selectedFirm?.id,
      },
      access_type: EDIT,
      remove_permission: false,
    };
    updatedPermissions.push(firmPermission);
  }

  return {
    user_id: userSelected?.id,
    role_type: roleType,
    permissions: updatedPermissions,
  };
};

export const saveUserPermissions = async ({
  userId,
  roleType,
  firmId,
  userPermissionsToSave,
}: SaveUserPermissionsParams) => {
  userPermissionsToSave.permissions.sort((a: any, b: any) =>
    a.feature_object.access_type < b.feature_object.access_type ? -1 : 1
  );
  const usersService = new UsersService();
  const response = await usersService.savePermissions([
    {
      user_id: userId,
      role_type: roleType,
      firm_id: firmId,
      permissions: transformPermissions({
        permissions: userPermissionsToSave?.permissions,
      }),
    },
  ]);
  if (!response) {
    throw new Error('An error occurred while saving user permissions');
  }
  return response;
};

const useSaveUserPermissions = () => {
  const queryClient = useQueryClient();
  const saveUserPermissionsMutation = useMutation({
    mutationFn: ({ userId, roleType, firmId, userPermissionsToSave }: SaveUserPermissionsParams) =>
      saveUserPermissions({
        userId,
        roleType,
        firmId,
        userPermissionsToSave,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['firmUsers']);
    },
  });

  const { mutate, isLoading, error, isSuccess } = saveUserPermissionsMutation;

  if (isSuccess) {
    saveUserPermissionsMutation.reset();
  }

  return { mutate, isLoading, error, isSuccess, updatePermissionsForCompOrFundRole };
};

const deleteCompanyUserPermissions = async (userId: number, companyId: number) => {
  const usersService = new UsersService();
  const response = await usersService.deleteCompanyUserPermissions(userId, companyId);
  if (!response) {
    throw new Error('An error occurred while deleting company user permissions');
  }
  return response;
};

const useDeleteCompanyUserPermissions = () => {
  const { successNotification } = useResponse();
  const queryClient = useQueryClient();
  const deleteCompanyUserPermissionsMutation = useMutation({
    mutationFn: ({ userId, companyId }: { userId: number; companyId: number }) =>
      deleteCompanyUserPermissions(userId, companyId),
    onSuccess: () => {
      successNotification('User was removed from the company successfully');
      queryClient.invalidateQueries(['firmUsers']);
    },
  });

  const { mutate, isLoading, error, isSuccess } = deleteCompanyUserPermissionsMutation;

  if (isSuccess) {
    deleteCompanyUserPermissionsMutation.reset();
  }

  return { mutate, isLoading, error, isSuccess };
};

export {
  useAddUserToFirm,
  useRemoveUserFromFirm,
  useCreateBulkUsers,
  useChangeUserFirmRole,
  useSaveUserPermissions,
  useDeleteCompanyUserPermissions,
};
